import { useEffect, useRef, useState, useCallback } from "react";
import { ArrowLeft, Gem, Target, Clock, X, Gift, Rocket, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ballSnipeApi, adsApi } from "@/api/mist";
import AlixAdReel from "@/components/ads/AlixAdReel";

// Ball Snipe — brick-breaker balls game. Physics run on <canvas> (client-only,
// $0). Persistent gems / adaptive weekly target / site-ad / reward come from the
// /api/games/ball-snipe backend. Only AD-earned gems persist.

const COLS = 7;
const AD_SECONDS = 30;
const AD_X_AT = 25;          // close button reveals at 25s (5s before the ad ends)

// Block colour flows across a spectrum by its CURRENT number — low→high:
// green → blue → yellow → orange → red. As a block gets hit its number drops and
// the colour gradually shifts back down toward green.
const _STOPS = ["#22c55e", "#3b82f6", "#eab308", "#f97316", "#dc2626"];
const _hx = (h) => { h = h.replace("#", ""); return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]; };
const _lerpHex = (a, b, t) => { const A = _hx(a), B = _hx(b); const c = A.map((x, i) => Math.round(x + (B[i] - x) * t)); return `rgb(${c[0]},${c[1]},${c[2]})`; };
const BLOCK_COLORS = (hp) => {
  const v = Math.max(0, Math.min(1, (hp - 1) / 23));   // hp 1 → green, hp 24+ → red
  const seg = v * (_STOPS.length - 1);
  const i = Math.min(_STOPS.length - 2, Math.floor(seg));
  return _lerpHex(_STOPS[i], _STOPS[i + 1], seg - i);
};

// 3 rotating default in-app ads (future paid business ad-space). One is picked at
// random per ad break. The referral QR reveals only in the final 5s (screenshot it).
const ADS = [
  {
    id: "creator",
    headline: "Build. Create. Go viral.",
    sub: "Everything Misteck gives you 👇",
    items: [
      { t: "🎬 Mist Video Editor", d: "Trim, stylize (anime/comic), stabilize & clean audio — free, on-device." },
      { t: "🪄 AI Studios", d: "Generate images, music, voiceovers & full reels with ALIX." },
      { t: "📱 Reels & Feed", d: "Publish in one tap and grow on the Mist feed." },
    ],
  },
  {
    id: "earn",
    headline: "Turn your clips into cash.",
    sub: "Create it. Post it. Get paid.",
    items: [
      { t: "💸 Creator Payouts", d: "Earn from your reels & posts across the Misteck feed." },
      { t: "🎮 Play Ball Snipe", d: "Smash blocks, bank gems, unlock premium tools for free." },
      { t: "🏷️ Sell & Promote", d: "Burn your link onto reels and drive traffic to your brand." },
    ],
  },
  {
    id: "pocket",
    headline: "Your whole studio, in your pocket.",
    sub: "No downloads. No cost to start.",
    items: [
      { t: "🔒 Secure Vault", d: "Your media, receipts & outros — private and organized." },
      { t: "🗣️ AI Voice & Music", d: "Voiceovers, cloning and instrumental beds in seconds." },
      { t: "✨ Comic & Anime FX", d: "One-tap AnimeGANv2 & comic filters on any clip." },
    ],
  },
];

export default function BallSnipe({ onBack }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const g = useRef(null);            // mutable engine state
  const rafRef = useRef(0);

  const [level, setLevel] = useState(1);
  const [ballCount, setBallCount] = useState(1);
  const [score, setScore] = useState(0);
  const [phase, setPhase] = useState("aim");   // aim | fire | over
  const [hud, setHud] = useState({ gems: 0, target: 200, seconds_left: 0, reward_pool: [], pending_reward: false });

  // ad + reward modals
  const [ad, setAd] = useState(null);          // {context, remain, canClose, qr, link}
  const [reward, setReward] = useState(null);  // {pool:[{feature,label}]}
  const [claiming, setClaiming] = useState(false);

  const refreshState = useCallback(async () => {
    try { const s = await ballSnipeApi.state(); setHud(s); if (s.pending_reward) setReward({ pool: s.reward_pool }); } catch { /* offline ok */ }
  }, []);

  useEffect(() => { refreshState(); }, [refreshState]);

  // Gems collect in small random amounts (1-5, 5 rare) as you clear levels.
  // After a bomb-out you must climb back to your best level before full gems
  // resume (server-enforced soft gate — a small trickle can still happen).
  const collectGems = useCallback(async (lvl) => {
    try {
      const r = await ballSnipeApi.collect(lvl, "ball_snipe", 1);
      setHud(r);
      if (r.reward_ready) setReward({ pool: r.reward_pool });
    } catch { /* offline ok */ }
  }, []);
  const collectRef = useRef(collectGems);
  collectRef.current = collectGems;

  // ---- engine helpers ----
  const spawnRow = useCallback((lvl) => {
    const e = g.current;
    const density = Math.min(6, 3 + Math.floor(lvl / 4));
    const cells = Array.from({ length: COLS }, (_, c) => c);
    // shuffle, take `density` blocks
    for (let i = cells.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [cells[i], cells[j]] = [cells[j], cells[i]]; }
    const chosen = cells.slice(0, density);
    chosen.forEach((c) => {
      const hp = Math.max(1, lvl + (Math.random() < 0.3 ? Math.floor(Math.random() * 3) : 0));
      e.blocks.push({ c, r: 0, hp, max: hp });
    });
    // exactly one +1 ball pickup per level, in a free column
    const free = [0, 1, 2, 3, 4, 5, 6].filter((c) => !chosen.includes(c));
    const pc = free.length ? free[Math.floor(Math.random() * free.length)] : Math.floor(Math.random() * COLS);
    e.pickups.push({ c: pc, r: 0 });
  }, []);

  const resetGame = useCallback(() => {
    const cvs = canvasRef.current; if (!cvs) return;
    const W = cvs.width, H = cvs.height, cell = W / COLS;
    g.current = {
      W, H, cell, topOffset: cell * 0.6,
      blocks: [], pickups: [], balls: [],
      ballCount: 1, launchX: W / 2, nextLaunchX: null,
      level: 1, phase: "aim", aim: null, toLaunch: 0, launchAcc: 0, firstDownX: null,
      rowsMax: Math.floor((H - 60 - cell * 0.6) / cell),
    };
    spawnRow(1);
    setLevel(1); setBallCount(1); setScore(0); setPhase("aim");
  }, [spawnRow]);

  // ---- resolve end of a turn: shift rows down, spawn new, check game over ----
  const resolveTurn = useCallback(() => {
    const e = g.current;
    e.launchX = e.firstDownX != null ? e.firstDownX : e.launchX;
    e.firstDownX = null;
    e.level += 1;
    e.blocks.forEach((b) => (b.r += 1));
    e.pickups.forEach((p) => (p.r += 1));
    // drop collected/offscreen pickups
    e.pickups = e.pickups.filter((p) => p.r < e.rowsMax + 1);
    const over = e.blocks.some((b) => b.r >= e.rowsMax);
    if (over) { e.phase = "over"; setPhase("over"); return; }
    spawnRow(e.level);
    setLevel(e.level);
    collectRef.current?.(e.level);
    e.phase = "aim"; setPhase("aim");
    // Ad gate: ONLY when a multiple-of-10 level is completed (10, 20, 30, …).
    if (e.level > 0 && e.level % 10 === 0) openAd("level_gate", e.level);
  }, [spawnRow]);

  // ---- fire balls ----
  const fireAt = useCallback((tx, ty) => {
    const e = g.current;
    if (!e || e.phase !== "aim") return;
    const sx = e.launchX, sy = e.H - 34;
    let dx = tx - sx, dy = ty - sy;
    if (dy > -8) dy = -8;                    // never fire downward
    const len = Math.hypot(dx, dy) || 1;
    // A touch quicker, still clearly visible travel.
    const speed = 4.7;
    e.dir = { x: (dx / len) * speed, y: (dy / len) * speed };
    e.toLaunch = e.ballCount;
    e.launchAcc = 0;
    e.balls = [];
    e.phase = "fire"; setPhase("fire");
  }, []);

  // ---- animation loop ----
  useEffect(() => {
    const cvs = canvasRef.current, wrap = wrapRef.current;
    if (!cvs || !wrap) return;
    const size = () => {
      const w = Math.min(wrap.clientWidth, 460);
      const h = Math.max(360, wrap.clientHeight - 4);
      cvs.width = w; cvs.height = h;
    };
    size();
    resetGame();

    const ctx = cvs.getContext("2d");
    const R = () => g.current.cell * 0.19;   // bigger, chunkier orbs

    const step = () => {
      const e = g.current;
      if (!e) { rafRef.current = requestAnimationFrame(step); return; }
      const { W, H, cell, topOffset } = e;
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "#0b1020"; ctx.fillRect(0, 0, W, H);

      // blocks
      e.blocks.forEach((b) => {
        const x = b.c * cell + 3, y = topOffset + b.r * cell + 3, s = cell - 6;
        ctx.fillStyle = BLOCK_COLORS(b.hp);
        ctx.beginPath(); ctx.roundRect(x, y, s, s, 2); ctx.fill();
        ctx.fillStyle = "#ffffff"; ctx.font = `bold ${Math.floor(cell * 0.32)}px monospace`;
        ctx.shadowColor = "rgba(0,0,0,0.35)"; ctx.shadowBlur = 2;
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(String(b.hp), x + s / 2, y + s / 2 + 1);
        ctx.shadowBlur = 0;
      });
      // static pickups — hollow WHITE ring (collect to +1 ammo), like Balls Bounce
      e.pickups.forEach((p) => {
        const cx = p.c * cell + cell / 2, cy = topOffset + p.r * cell + cell / 2;
        const pr = cell * 0.26;
        ctx.strokeStyle = "#ffffff"; ctx.lineWidth = Math.max(3, cell * 0.06);
        ctx.beginPath(); ctx.arc(cx, cy, pr, 0, 7); ctx.stroke();
        ctx.strokeStyle = "rgba(255,255,255,0.4)"; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(cx, cy, pr * 0.5, 0, 7); ctx.stroke();
      });

      // launcher — the next WHITE ammo orb, ready to fire
      const lr = R();
      ctx.fillStyle = "#ffffff"; ctx.beginPath(); ctx.arc(e.launchX, H - 34, lr, 0, 7); ctx.fill();

      // aim guide — dotted trajectory line (extended past the touch point)
      if (e.phase === "aim" && e.aim) {
        let dx = e.aim.x - e.launchX, dy = e.aim.y - (H - 34);
        if (dy > -8) dy = -8;
        const len = Math.hypot(dx, dy) || 1; const ex = dx / len, ey = dy / len;
        ctx.strokeStyle = "rgba(255,255,255,0.85)"; ctx.lineCap = "round";
        ctx.setLineDash([1, 12]); ctx.lineWidth = 4;
        ctx.beginPath(); ctx.moveTo(e.launchX, H - 34);
        ctx.lineTo(e.launchX + ex * (H * 0.9), (H - 34) + ey * (H * 0.9)); ctx.stroke();
        ctx.setLineDash([]); ctx.lineCap = "butt";
      }
      // ball-count label by the cannon (e.g. x66)
      ctx.fillStyle = "#ffffff"; ctx.font = `bold ${Math.floor(cell * 0.34)}px monospace`;
      ctx.textAlign = "left"; ctx.textBaseline = "middle";
      ctx.fillText(`x${e.ballCount}`, e.launchX + R() + 6, H - 34);

      // launch queued balls
      if (e.phase === "fire") {
        e.launchAcc += 1;
        if (e.toLaunch > 0 && e.launchAcc % 10 === 0) {
          e.balls.push({ x: e.launchX, y: H - 34, vx: e.dir.x, vy: e.dir.y, cool: {} });
          e.toLaunch -= 1;
        }
        const r = R();
        for (let i = e.balls.length - 1; i >= 0; i--) {
          const ball = e.balls[i];
          ball.x += ball.vx; ball.y += ball.vy;
          if (ball.x < r) { ball.x = r; ball.vx = Math.abs(ball.vx); }
          if (ball.x > W - r) { ball.x = W - r; ball.vx = -Math.abs(ball.vx); }
          if (ball.y < r) { ball.y = r; ball.vy = Math.abs(ball.vy); }
          // pickups
          for (let k = e.pickups.length - 1; k >= 0; k--) {
            const p = e.pickups[k];
            const cx = p.c * cell + cell / 2, cy = topOffset + p.r * cell + cell / 2;
            if (Math.hypot(ball.x - cx, ball.y - cy) < cell * 0.22 + r) {
              e.pickups.splice(k, 1); e.ballCount += 1; setBallCount(e.ballCount);
            }
          }
          // blocks — AABB reflection
          for (let k = e.blocks.length - 1; k >= 0; k--) {
            const b = e.blocks[k];
            const bx = b.c * cell + 3, by = topOffset + b.r * cell + 3, s = cell - 6;
            if (ball.x + r > bx && ball.x - r < bx + s && ball.y + r > by && ball.y - r < by + s) {
              const overL = ball.x + r - bx, overR = bx + s - (ball.x - r);
              const overT = ball.y + r - by, overB = by + s - (ball.y - r);
              const m = Math.min(overL, overR, overT, overB);
              if (m === overL) { ball.x = bx - r; ball.vx = -Math.abs(ball.vx); }
              else if (m === overR) { ball.x = bx + s + r; ball.vx = Math.abs(ball.vx); }
              else if (m === overT) { ball.y = by - r; ball.vy = -Math.abs(ball.vy); }
              else { ball.y = by + s + r; ball.vy = Math.abs(ball.vy); }
              b.hp -= 1; e._score = (e._score || 0) + 1; setScore(e._score);
              if (b.hp <= 0) e.blocks.splice(k, 1);
              break;
            }
          }
          // fell out the bottom
          if (ball.y - r > H) {
            if (e.firstDownX == null) e.firstDownX = Math.max(r, Math.min(W - r, ball.x));
            e.balls.splice(i, 1);
          }
          ctx.fillStyle = "#ffffff";
          ctx.beginPath(); ctx.arc(ball.x, ball.y, r, 0, 7); ctx.fill();
          ctx.strokeStyle = "rgba(0,0,0,0.25)"; ctx.lineWidth = 1.5;
          ctx.beginPath(); ctx.arc(ball.x, ball.y, r, 0, 7); ctx.stroke();
        }
        if (e.toLaunch === 0 && e.balls.length === 0) resolveTurn();
      }

      // launch line
      ctx.strokeStyle = "rgba(255,255,255,0.12)"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, H - 22); ctx.lineTo(W, H - 22); ctx.stroke();

      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- pointer aiming ----
  const pt = (ev) => {
    const cvs = canvasRef.current; const rect = cvs.getBoundingClientRect();
    const c = ev.touches ? ev.touches[0] : ev;
    return { x: (c.clientX - rect.left) * (cvs.width / rect.width), y: (c.clientY - rect.top) * (cvs.height / rect.height) };
  };
  const onDown = (ev) => { if (g.current?.phase !== "aim") return; g.current.aim = pt(ev); };
  const onMove = (ev) => { if (g.current?.phase !== "aim" || !g.current.aim) return; ev.preventDefault(); g.current.aim = pt(ev); };
  const onUp = (ev) => {
    const e = g.current; if (!e || e.phase !== "aim") return;
    const p = e.aim || pt(ev); e.aim = null; fireAt(p.x, p.y);
  };

  // ---- ad flow ----
  const adTimer = useRef(null);
  const openAd = async (context, lvl) => {
    let qr = "", link = "";
    try { const r = await ballSnipeApi.qr(); qr = r.qr; link = r.link; } catch { /* qr optional */ }
    let sponsor = null;
    try { const s = await adsApi.serveGame(); if (s.slot) sponsor = s.slot; } catch { /* none booked */ }
    const creative = ADS[Math.floor(Math.random() * ADS.length)];
    setAd({ context, lvl, remain: AD_SECONDS, canClose: false, qr, link, creative, sponsor });
    clearInterval(adTimer.current);
    adTimer.current = setInterval(() => {
      setAd((a) => {
        if (!a) return a;
        const remain = a.remain - 1;
        return { ...a, remain: Math.max(0, remain), canClose: remain <= AD_SECONDS - AD_X_AT };
      });
    }, 1000);
  };
  const closeAd = async () => {
    const a = ad; clearInterval(adTimer.current); setAd(null);
    try {
      const res = await ballSnipeApi.adReward(a?.lvl || level, a?.context || "level_gate");
      setHud(res);
      toast.success(`+${res.gems_added} 💎 gems!`);
      if (a?.context === "revive") reviveContinue();
      if (res.reward_ready) { setReward({ pool: res.reward_pool }); }
    } catch { toast.error("Couldn't bank your gems"); if (a?.context === "revive") reviveContinue(); }
  };
  useEffect(() => () => clearInterval(adTimer.current), []);

  const reviveContinue = () => {
    const e = g.current; if (!e) return;
    const maxR = Math.max(0, ...e.blocks.map((b) => b.r));
    e.blocks = e.blocks.filter((b) => b.r < maxR - 1);       // clear bottom 2 rows
    e.phase = "aim"; setPhase("aim");
  };

  const claim = async (feature) => {
    setClaiming(true);
    try { const r = await ballSnipeApi.claimReward(feature); toast.success(r.message || "Reward unlocked!"); setReward(null); refreshState(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't claim reward"); }
    finally { setClaiming(false); }
  };

  const days = Math.floor((hud.seconds_left || 0) / 86400);
  const hrs = Math.floor(((hud.seconds_left || 0) % 86400) / 3600);

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-[#0b1020]" data-testid="ball-snipe">
      {/* HUD */}
      <div className="flex items-center gap-2 border-b-2 border-white/10 px-3 py-2.5">
        <button onClick={onBack} data-testid="ball-snipe-back" className="grid h-9 w-9 place-items-center rounded-xl border-2 border-white/30 bg-white/5 active:scale-95"><ArrowLeft size={16} className="text-white" /></button>
        <div className="flex items-center gap-1.5 rounded-lg border-2 border-violet-400/40 bg-violet-500/10 px-2 py-1" data-testid="ball-snipe-gems">
          <Gem size={14} className="text-violet-300" /><span className="font-mono text-sm font-black text-white">{hud.gems}</span>
          <span className="font-mono text-[9px] text-white/40">/ {hud.target}</span>
        </div>
        <div className="flex items-center gap-1 rounded-lg border-2 border-white/15 px-2 py-1" data-testid="ball-snipe-timer">
          <Clock size={12} className="text-white/50" /><span className="font-mono text-[10px] font-bold text-white/70">{days}d {hrs}h</span>
        </div>
        <div className="ml-auto flex items-center gap-1 rounded-lg border-2 border-gold/40 bg-gold/10 px-2 py-1">
          <Target size={12} className="text-gold" /><span className="font-mono text-[11px] font-black text-white">Lv {level}</span>
          <span className="font-mono text-[9px] text-white/40">· {ballCount}●</span>
        </div>
      </div>

      {/* Board */}
      <div ref={wrapRef} className="relative flex-1 min-h-0 flex items-stretch justify-center p-1">
        <canvas ref={canvasRef} data-testid="ball-snipe-canvas"
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}
          className="rounded-xl border-2 border-white/10 touch-none w-full" style={{ maxWidth: 460 }} />
        <div className="pointer-events-none absolute bottom-3 right-4 font-mono text-[11px] font-black text-white/60" data-testid="ball-snipe-score">×{score}</div>
        {phase === "aim" && (
          <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 font-mono text-[9px] uppercase tracking-widest text-white/40">Drag to aim · release to fire</div>
        )}
      </div>

      {/* GAME OVER */}
      {phase === "over" && (
        <div className="absolute inset-0 z-40 grid place-items-center bg-black/85 p-6" data-testid="ball-snipe-gameover">
          <div className="w-full max-w-[320px] rounded-2xl border-2 border-white/20 bg-[#141a2e] p-5 text-center">
            <p className="font-serif text-2xl text-white">Blocks hit the bottom!</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/40">Reached level {level} · best {hud.best_level || 0}</p>
            <button onClick={() => openAd("revive", level)} data-testid="ball-snipe-revive-ad"
              className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl border-2 border-violet-400 bg-violet-500 py-3 font-mono text-[11px] font-black uppercase tracking-widest text-white active:scale-[0.98]">
              ▶ Watch ad · clear 2 rows & continue
            </button>
            <button onClick={resetGame} data-testid="ball-snipe-restart"
              className="mt-2 w-full rounded-xl border-2 border-white/25 bg-white/5 py-3 font-mono text-[11px] font-black uppercase tracking-widest text-white/80 active:scale-[0.98]">
              Start fresh
            </button>
            <p className="mt-3 font-mono text-[8px] uppercase tracking-widest text-white/30">Gems only trickle until you climb back to level {hud.best_level || 0}</p>
          </div>
        </div>
      )}

      {/* SITE AD */}
      {ad && (
        <div className="absolute inset-0 z-50 flex flex-col bg-gradient-to-b from-[#1a1030] to-[#0b1020]" data-testid="ball-snipe-ad">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
            <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-violet-300">Sponsored · Misteck</span>
            <span className="ml-auto font-mono text-[11px] font-black text-white/70" data-testid="ball-snipe-ad-timer">{ad.remain}s</span>
            {ad.canClose && (
              <button onClick={closeAd} data-testid="ball-snipe-ad-close" className="grid h-8 w-8 place-items-center rounded-lg border-2 border-white/40 bg-white/10 active:scale-95"><X size={16} className="text-white" /></button>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
            {ad.sponsor && (
              <button onClick={() => { adsApi.click(ad.sponsor.id, 'game').then((r) => { if (r?.target_url) window.open(r.target_url, "_blank"); }); }}
                data-testid="ball-snipe-sponsor" className="mb-4 block w-full overflow-hidden rounded-2xl border-2 border-gold/50 bg-black/40 text-left">
                {ad.sponsor.media_kind === "video" ? (
                  <video src={`${process.env.REACT_APP_BACKEND_URL}/api/ads/media/${ad.sponsor.id}`} autoPlay muted loop playsInline className="w-full object-cover max-h-44" />
                ) : (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/ads/media/${ad.sponsor.id}`} alt={ad.sponsor.title} className="w-full object-cover max-h-44" />
                )}
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="font-serif text-sm text-white">{ad.sponsor.title}</span>
                  <span className="font-mono text-[8px] uppercase tracking-widest text-gold">Learn more →</span>
                </div>
              </button>
            )}
            <h2 className="font-serif text-3xl text-white leading-tight">{ad.creative?.headline}</h2>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-violet-300/80">{ad.creative?.sub}</p>
            <div className="mt-4 space-y-2.5">
              {(ad.creative?.items || []).map((f) => (
                <div key={f.t} className="rounded-xl border-2 border-white/10 bg-white/5 p-3">
                  <p className="font-serif text-base text-white">{f.t}</p>
                  <p className="mt-0.5 font-mono text-[10px] leading-snug text-white/60">{f.d}</p>
                </div>
              ))}
            </div>
            {!ad.sponsor && <AlixAdReel onJoin={closeAd} className="mt-4" />}
            {ad.canClose ? (
              <div className="mt-5 rounded-2xl border-2 border-gold/40 bg-black/40 p-4 text-center" data-testid="ball-snipe-ad-qr">
                <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Invite a friend · screenshot this QR</p>
                <p className="font-mono text-[8px] tracking-wide text-white/40 mt-0.5">They join → you both win a free tool use</p>
                {ad.qr ? <img src={ad.qr} alt="Your referral QR" className="mx-auto mt-3 h-40 w-40 rounded-lg bg-white p-2" />
                  : <div className="mx-auto mt-3 grid h-40 w-40 place-items-center rounded-lg bg-white/10"><Loader2 className="animate-spin text-white/40" /></div>}
                {ad.link && (
                  <button onClick={() => { navigator.clipboard?.writeText(ad.link); toast.success("Referral link copied"); }} data-testid="ball-snipe-ad-copy"
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg border-2 border-white/25 bg-white/5 px-3 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-white/80">
                    <Copy size={11} /> Copy link
                  </button>
                )}
              </div>
            ) : (
              <p className="mt-5 text-center font-mono text-[9px] uppercase tracking-widest text-white/30" data-testid="ball-snipe-ad-qr-hint">
                📸 Your invite QR appears in the last {AD_SECONDS - AD_X_AT}s…
              </p>
            )}
          </div>
        </div>
      )}

      {/* REWARD CLAIM */}
      {reward && !ad && (
        <div className="absolute inset-0 z-50 grid place-items-center bg-black/85 p-6" data-testid="ball-snipe-reward">
          <div className="w-full max-w-[340px] rounded-2xl border-2 border-gold/60 bg-[#141a2e] p-5 text-center">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border-2 border-gold bg-gold/15"><Gift size={26} className="text-gold" /></div>
            <h3 className="mt-3 font-serif text-2xl text-white">You hit your gem target! 🎉</h3>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/50">Pick 1 tool — one free use, on us</p>
            <div className="mt-4 space-y-2">
              {(reward.pool || []).map((tool) => (
                <button key={tool.feature} onClick={() => claim(tool.feature)} disabled={claiming} data-testid={`ball-snipe-claim-${tool.feature}`}
                  className="w-full flex items-center gap-2 rounded-xl border-2 border-white/20 bg-white/5 px-4 py-3 text-left active:scale-[0.98] disabled:opacity-50">
                  <Rocket size={15} className="text-gold shrink-0" />
                  <span className="flex-1 font-mono text-[11px] font-black uppercase tracking-wide text-white">{tool.label}</span>
                  {claiming ? <Loader2 size={14} className="animate-spin text-white/50" /> : <span className="font-mono text-[8px] text-white/40">1× free</span>}
                </button>
              ))}
            </div>
            <button onClick={() => setReward(null)} data-testid="ball-snipe-reward-later" className="mt-3 font-mono text-[9px] uppercase tracking-widest text-white/40">Maybe later</button>
          </div>
        </div>
      )}
    </div>
  );
}
